import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { ExaminationResponse } from '@pixacare/dtos';
import { FormAnswers, getReport } from '@pixacare/forms';
import { ExaminationsHttpService } from '@services/http/examinations.http.service';
import { RequestResult } from '@shared/request-state/models/request-result';
import { joinRequestResult } from '@shared/request-state/rx-pipes/join-request-result.pipe';
import { trackRequestResult } from '@shared/request-state/rx-pipes/track-request-result.pipe';
import { withRequestResult } from '@shared/request-state/rx-pipes/with-request-result.pipe';
import { examinationsActions } from '@shared/store/examinations/examinations.actions';
import { selectExaminations } from '@shared/store/examinations/examinations.selector';
import { selectHasLicenseWriteAccess } from '@shared/store/licenses/licenses.selectors';
import { selectPatient } from '@shared/store/patients/patients.selectors';
import { BehaviorSubject, combineLatest, defer, iif, map, Observable, skipWhile, Subject, switchMap, tap } from 'rxjs';
import { ExaminationDisplay } from '../models/examination-display';
import { ExaminationDisplaysByExaminationFormId } from '../models/examination-displays-by-examination-form-id';
import { ExaminationFormResponses } from '../models/examination-forms';
import { ExaminationServiceProps } from '../models/examinations-service-props';

@Injectable()
export class ExaminationsService {
  private readonly examinationsHttpService = inject(ExaminationsHttpService);
  private readonly store = inject(Store);
  private readonly translocoService = inject(TranslocoService);

  readonly description = this.translocoService.translate(`Documentez les antécédents, l'histoire de la maladie
  ou d'autres informations relatives au patient pour que vos collaborateurs puissent les retrouver facilement.`);

  private readonly _props$ = new BehaviorSubject<ExaminationServiceProps>(null);

  readonly examinationForms$: Observable<ExaminationFormResponses> = this._props$.pipe(
    skipWhile((props) => !props),
    switchMap(({ departmentId, clientCode }) => this.getExaminationForms(departmentId, clientCode)),
  );

  readonly examinationsState$: Observable<RequestResult<ExaminationDisplaysByExaminationFormId>> = this._props$.pipe(
    skipWhile((props) => !props),
    switchMap((props) => this.getExaminations(props)),
  );

  readonly examinationsCountState$: Observable<RequestResult<number>> = this.examinationsState$.pipe(
    map((state) => this.getExaminationsCountState(state)),
  );

  readonly canWriteExamination$: Observable<boolean> = this._props$.pipe(
    skipWhile((props) => !props),
    switchMap(({ clientCode, patientId }) => this.canWriteExamination(clientCode, patientId)),
  );

  readonly examinationCreated$ = new Subject<ExaminationResponse>();

  get props$(): Observable<ExaminationServiceProps> {
    return this._props$.asObservable();
  }

  init({ fetch = true, ...props }: ExaminationServiceProps & { fetch?: boolean }): void {
    if (fetch) {
      this.fetchExaminations(props);
    }
    this._props$.next(props);
  }

  createExamination(
    examinationFormId: number,
    formAnswers: FormAnswers,
  ): Observable<RequestResult<ExaminationResponse>> {
    return this._props$.pipe(
      switchMap(({ clientCode, patientId, departmentId }) => {
        const payload = {
          clientCode,
          patientId,
          examinationFormId,
          formAnswers,
        };

        return this.examinationsHttpService.createExamination(payload).pipe(
          tap((examinationResponse) => {
            this.store.dispatch(
              examinationsActions.addExamination({
                clientCode,
                examinationResponse,
                departmentId,
              }),
            );
            this.examinationCreated$.next(examinationResponse);
          }),
          withRequestResult(),
        );
      }),
    );
  }

  updateExamination(examinationId: number, formAnswers: FormAnswers): Observable<RequestResult<ExaminationResponse>> {
    return this.props$.pipe(
      switchMap(({ clientCode, patientId, departmentId }) => {
        const payload = {
          clientCode,
          patientId,
          examinationId,
          formAnswers,
        };

        return this.examinationsHttpService.updateExamination(payload).pipe(
          tap((examinationResponse) => {
            this.store.dispatch(
              examinationsActions.addExamination({
                clientCode,
                examinationResponse,
                departmentId,
              }),
            );
          }),
          withRequestResult(),
        );
      }),
    );
  }

  deleteExamination(examinationId: number): Observable<void> {
    return this._props$.pipe(
      switchMap(({ departmentId, clientCode, patientId }) =>
        this.examinationsHttpService.deleteExamination({ clientCode, patientId, examinationId }).pipe(
          tap(() => {
            this.store.dispatch(
              examinationsActions.deleteExamination({
                clientCode,
                patientId,
                departmentId,
                examinationId,
              }),
            );
          }),
        ),
      ),
    );
  }

  private canWriteExamination(clientCode: string, patientId: number): Observable<boolean> {
    return combineLatest([
      this.store.select(selectHasLicenseWriteAccess),
      this.store.select(selectPatient(clientCode, patientId)),
    ]).pipe(map(([hasLicenseWriteAccess, patient]) => hasLicenseWriteAccess && !patient?.isArchived));
  }

  private getExaminationsCountState(
    state: RequestResult<ExaminationDisplaysByExaminationFormId>,
  ): RequestResult<number> {
    if (!state.isSuccess || !state.data) {
      return {
        ...state,
        data: null,
      };
    }

    const count = Object.values(state.data).reduce((acc, examinations) => acc + examinations.length, 0);

    return {
      ...state,
      data: count,
    };
  }

  private getExaminationForms(departmentId: number, clientCode: string): Observable<ExaminationFormResponses> {
    return iif(
      () => !departmentId,
      defer(() => this.examinationsHttpService.getUserExaminationForms({ clientCode, withForms: true })),
      defer(() =>
        this.examinationsHttpService.getDepartmentExaminationForms({
          clientCode,
          departmentId,
          withForms: true,
        }),
      ),
    ).pipe(
      map((examinations) =>
        examinations.reduce((acc, examination) => {
          acc[examination.id] = examination;
          return acc;
        }, {}),
      ),
    );
  }

  private fetchExaminations: (props: ExaminationServiceProps) => void = ({ departmentId, clientCode, patientId }) => {
    iif(
      () => !departmentId,
      defer(() => this.examinationsHttpService.getUserExaminations({ clientCode, patientId, withForms: false })),
      defer(() =>
        this.examinationsHttpService.getDepartmentExaminations({
          patientId,
          clientCode,
          departmentId,
          withForms: false,
        }),
      ),
    )
      .pipe(
        tap((examinations) =>
          this.store.dispatch(
            examinationsActions.updateExaminationsState({
              clientCode,
              patientId,
              examinations,
              departmentId,
            }),
          ),
        ),
        trackRequestResult(['examinations']),
      )
      .subscribe();
  };

  private getExaminations({
    departmentId,
    clientCode,
    patientId,
  }: ExaminationServiceProps): Observable<RequestResult<ExaminationDisplaysByExaminationFormId>> {
    const examinations$ = this.store
      .select(selectExaminations({ clientCode, patientId, departmentId }))
      .pipe(joinRequestResult(['examinations']));

    return combineLatest([examinations$, this.examinationForms$]).pipe(
      map(([examinationsState, examinationForms]) => ({
        ...examinationsState,
        data: examinationsState?.data
          ? Object.keys(examinationsState?.data).reduce((acc, examinationFormId) => {
              acc[examinationFormId] = this.fillExaminationsFormReports(
                examinationsState?.data[examinationFormId],
                examinationForms,
              );
              return acc;
            }, {} as ExaminationDisplaysByExaminationFormId)
          : {},
      })),
    );
  }

  private fillExaminationsFormReports(
    examinations: ExaminationDisplay[],
    examinationForms: ExaminationFormResponses,
  ): ExaminationDisplay[] {
    return examinations.map((examination) => ({
      ...examination,
      formReport: examination.formAnswer
        ? getReport(examinationForms[examination.examinationFormId]?.form, examination.formAnswer)
        : null,
    }));
  }
}
